// src/pages/Instance/.../Invoicing/index.jsx
import React, { useEffect, useState, useContext, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import axios from "axios";
import { AuthContext } from "../../../../helpers/AuthContext";

import {
  Box,
  Button,
  Chip,
  TextField,
  Typography,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Menu,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Skeleton,
  Stack,
  Divider,
  InputAdornment,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import LinkIcon from "@mui/icons-material/Link";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import FolderOffIcon from "@mui/icons-material/FolderOff";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import SettingsIcon from "@mui/icons-material/Settings";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  UITableShell,
  UITableToolbar,
  UITableActionCell,
  UIStatusChip,
} from "../../../../components/ui";
import {
  filterInvoicesForFinanceView,
  getInvoiceRole,
  isDepositInvoiceRole,
} from "../../../../utils/invoiceDeposits";
import { isInactiveInvoice } from "../../../../utils/invoiceVisibility";
import { fetchFinanceInvoices } from "../../../../services/invoices";
import { FINANCE_INVOICE_STATUS, filterFinanceInvoicesByStatus } from "./invoiceStatusFilter";
import { buildFinanceInvoicingRowLinks } from "./invoiceRowLinks";
import { downloadDepositRegisterCsv, printDepositRegister, printInvoiceRegister } from "./depositRegister";

// 🔗 QBO link dialog
import QboCustomerLinkDialog from "../../../../components/QboCustomerLinkDialog";

// const VIEW = { INVOICES: "invoices", EXPORTED: "exported" };

const VIEW = {
  INVOICES: "invoices",
  DEPOSITS: "deposits",
  EXPORTED: "exported",
  LOGS: "logs",
};

const QB_ICON =
  "https://cdn-1.webcatalog.io/catalog/quickbooks/quickbooks-icon-filled-256.webp?v=1757897011406";

const QboDot = ({ linked }) => {
  const ok = Boolean(linked);
  return (
    <span
      title={ok ? "QBO linked" : "QBO not linked"}
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: ok ? "#2e7d32" : "#d32f2f",
        marginRight: 6,
        flex: "0 0 auto",
      }}
    />
  );
};

const parseMaybeJsonObject = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const parseInvoiceRelationships = parseMaybeJsonObject;
const formatDepositCurrency = (value) => `$${(Number(value) || 0).toFixed(2)}`;

const normalizeIdList = (value) => {
  if (Array.isArray(value)) return value.filter((v) => v != null && String(v).trim() !== "").map(String);
  if (value == null || value === "") return [];
  return String(value).split(",").map((v) => v.trim()).filter(Boolean);
};

const getDepositConsolidationHint = () =>
  "Deposit invoices are consolidated into the follow-up invoice and are not exported separately to QBO.";

const getInvoiceDepositHistory = (invoice) => {
  const rel = parseInvoiceRelationships(invoice?.invoiceRelationships);
  const accountingRef = parseMaybeJsonObject(invoice?.accountingRef);
  const history = accountingRef?.thirdParty?.invoiceHistory || {};
  const numberOrNull = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };
  const fromCents = (value) => {
    const n = numberOrNull(value);
    return n == null ? null : n / 100;
  };
  const priorDepositPaidRaw =
    rel?.priorDepositPaid ??
    history?.priorDepositPaid ??
    fromCents(history?.priorDepositPaidCents) ??
    0;
  const orderTotalRaw =
    rel?.orderTotal ??
    history?.orderTotal ??
    fromCents(rel?.orderTotalCents) ??
    fromCents(history?.orderTotalCents);
  const currentInvoiceAmountRaw =
    rel?.currentInvoiceAmount ??
    history?.currentInvoiceAmount ??
    invoice?.amount;
  const remainingAfterThisInvoiceRaw =
    rel?.remainingAfterThisInvoice ??
    history?.remainingAfterThisInvoice ??
    fromCents(rel?.remainingAfterThisInvoiceCents) ??
    fromCents(history?.remainingAfterThisInvoiceCents);
  const priorPaidNonDepositRaw =
    rel?.priorPaidNonDepositTotal ??
    history?.priorPaidNonDepositTotal ??
    fromCents(rel?.priorPaidNonDepositCents) ??
    fromCents(history?.priorPaidNonDepositCents) ??
    0;

  return {
    priorDepositPaid: numberOrNull(priorDepositPaidRaw) || 0,
    priorDepositInvoiceIds: normalizeIdList(rel?.priorDepositInvoiceIds ?? history?.priorDepositInvoiceIds),
    orderTotal: numberOrNull(orderTotalRaw),
    currentInvoiceAmount: numberOrNull(currentInvoiceAmountRaw),
    remainingAfterThisInvoice: numberOrNull(remainingAfterThisInvoiceRaw),
    priorPaidNonDeposit: numberOrNull(priorPaidNonDepositRaw) || 0,
    balanceKind: String(rel?.balanceKind || history?.balanceKind || "").toLowerCase(),
    renderMode: String(rel?.renderMode || history?.renderMode || "").toLowerCase(),
  };
};

const isPostPaymentDeltaInvoice = (invoice) => {
  const { balanceKind, renderMode } = getInvoiceDepositHistory(invoice);
  return balanceKind === "post_payment_delta" || renderMode === "delta_lines_only";
};

export const getQboAmountIndicator = (invoice, invoiceDepositMode) => {
  const amount = Number(invoice?.amount || 0);
  const role = getInvoiceRole(invoice);
  const history = getInvoiceDepositHistory(invoice);
  const isDepositInvoice = isDepositInvoiceRole(invoice);
  const isDelta = isPostPaymentDeltaInvoice(invoice);
  const isConsolidation = String(invoiceDepositMode || "").toLowerCase() === "consolidation";
  const alreadyExported = String(invoice?.qboExportStatus || "").toLowerCase() === "exported";

  if (isDepositInvoice) {
    return {
      show: true,
      label: "QBO",
      tone: "warning",
      title: isConsolidation
        ? "Deposit invoice is not exported separately to QBO. Its paid amount is carried into the later consolidated invoice."
        : "Deposit invoice is not exported separately to QBO.",
    };
  }

  if (!isConsolidation || history.priorDepositPaid <= 0) {
    return {
      show: false,
      label: "",
      tone: "neutral",
      title: "",
    };
  }

  if (isDelta) {
    return {
      show: true,
      label: "QBO",
      tone: "info",
      title: `QBO delta export. This invoice exports only the delta amount (${formatDepositCurrency(amount)}); prior deposits are not consolidated again.`,
    };
  }

  const inferredFullOrderTotal =
    history.orderTotal ??
    (
      Number.isFinite(history.currentInvoiceAmount)
        ? history.currentInvoiceAmount +
          history.priorDepositPaid +
          history.priorPaidNonDeposit +
          (Number.isFinite(history.remainingAfterThisInvoice) ? history.remainingAfterThisInvoice : 0)
        : null
    );
  const totalLabel = Number.isFinite(inferredFullOrderTotal)
    ? formatDepositCurrency(inferredFullOrderTotal)
    : null;
  const depositLabel = formatDepositCurrency(history.priorDepositPaid);
  const currentLabel = formatDepositCurrency(amount);
  const depositIdsLabel = history.priorDepositInvoiceIds.length
    ? ` Deposit invoice(s): ${history.priorDepositInvoiceIds.join(", ")}.`
    : "";

  if (!totalLabel) {
    return {
      show: true,
      label: "QBO",
      tone: "info",
      title:
        `QBO consolidated export. ${depositLabel} of prior deposit payments are attached to this invoice. ` +
        `Elite Core will resolve the full-order revenue basis at export time; if it cannot do so reliably, the QBO export is blocked rather than exporting an incomplete amount.${depositIdsLabel}`,
    };
  }

  return {
    show: true,
    label: "QBO",
    tone: alreadyExported ? "success" : "info",
    title:
      `QBO consolidated export. Full order revenue: ${totalLabel}. ` +
      `Deposit already received: ${depositLabel}. Remaining Elite invoice: ${currentLabel}. ` +
      `One consolidated QBO invoice is used; the deposit is not exported as a second invoice.` +
      depositIdsLabel +
      (alreadyExported ? " This invoice has already been exported to QBO." : ""),
  };
};

const getDepositAttachedTooltip = (invoice) => {
  const { priorDepositPaid, priorDepositInvoiceIds } = getInvoiceDepositHistory(invoice);
  return `Deposit attached: ${formatDepositCurrency(priorDepositPaid)}. Deposit invoice(s): ${priorDepositInvoiceIds.length ? priorDepositInvoiceIds.join(", ") : "—"}. This deposit will be consolidated into this QBO export.`;
};

const hasDepositAttachment = (invoice) => {
  if (isDepositInvoiceRole(invoice)) return false;
  const { priorDepositPaid, priorDepositInvoiceIds } = getInvoiceDepositHistory(invoice);
  return priorDepositPaid > 0 || priorDepositInvoiceIds.length > 0;
};

const getInvoiceTypeLabel = (invoice) => {
  const role = getInvoiceRole(invoice);
  if (isDepositInvoiceRole(invoice)) return "Deposit Invoice";
  if (role === "partial") return "Partial Invoice";
  if (role === "progress") return "Progress Invoice";
  if (role === "balance") return "Balance Invoice";
  if (role === "full") return "Full Invoice";
  return "Invoice";
};

export default function InstanceInvoicing() {
  const { instanceUniqueId } = useParams();
  const location = useLocation();
  const { authState } = useContext(AuthContext);

  const theme = useTheme();
  const C = {
    paperBg: theme.palette.background.paper,
    subtleBg: theme.palette.action.hover,
    border: theme.palette.divider,
    textPri: theme.palette.text.primary,
    textSec: theme.palette.text.secondary,
    textMuted: theme.palette.text.disabled,
    white: theme.palette.common.white,
    headerBg: theme.eliteCore?.tokens?.color?.neutral?.[800] ?? "#1e293b",
    headerBgHover: theme.eliteCore?.tokens?.color?.neutral?.[700] ?? "#334155",
  };

  // DEFAULT TAB → "search"
  const [activeTab, setActiveTab] = useState("search");
  const initialView = new URLSearchParams(location.search).get("view") === VIEW.DEPOSITS
    ? VIEW.DEPOSITS
    : VIEW.INVOICES;
  const [view, setView] = useState(initialView);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState(FINANCE_INVOICE_STATUS.ALL);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);

  const [qbo, setQbo] = useState({
    connected: false,
    realmId: null,
    env: null,
    suggestedEnv: null,
    modeMismatch: false,
    availableConnections: [],
    checking: false,
  });
  const [qboExportEnabled, setQboExportEnabled] = useState(false);
  const [invoiceDepositMode, setInvoiceDepositMode] = useState("consolidation");
  const [exportingId, setExportingId] = useState(null);
  const [qboPaymentSyncingId, setQboPaymentSyncingId] = useState(null);
  const [qboPaymentBatchSyncing, setQboPaymentBatchSyncing] = useState(false);

  // 🔗 Link-to-QBO dialog state
  const isQboCustomerLinked = (row, qboState = qbo) =>
    Boolean(
      qboState?.connected &&
      String(row?._qboLinkStatus || "").toLowerCase() === "linked" &&
      row?._qboCustomerId,
    );
  const hasStoredQboCustomerLink = (row) =>
    Boolean(
      String(row?._qboLinkStatus || "").toLowerCase() === "linked" &&
      row?._qboCustomerId,
    );
  const [qboLinkOpen, setQboLinkOpen] = useState(false);
  const [qboLinkCtx, setQboLinkCtx] = useState({
    customerId: null,
    customerName: "",
    invoiceId: null,
  });

  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [fileExportMenuEl, setFileExportMenuEl] = useState(null);
  const [reportMenuEl, setReportMenuEl] = useState(null);

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = {
    headers: { accessToken: localStorage.getItem("accessToken") },
  };

  // Nunito + Capitalize for all buttons
  const btnSx = {
    textTransform: "capitalize",
    fontFamily: '"Nunito", sans-serif',
  };

  const isAdministrator =
    String(authState?.role || "").toLowerCase() === "administrator";

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("asc");

  const [density, setDensity] = useState("comfortable");
  const [densityMenuEl, setDensityMenuEl] = useState(null);

  const densitySettings = {
    compact: { py: 0.75, fontSize: "0.8125rem" },
    comfortable: { py: 1.5, fontSize: "0.875rem" },
    spacious: { py: 2.25, fontSize: "0.9375rem" },
  };

  const hydrateQboLinkStatus = async (list) => {
    const out = await Promise.all(
      list.map(async (r) => {
        try {
          const { data } = await axios.get(
            `${SERVER}/invoices/${r.id}/customer-summary`,
            {
              ...headers,
              params: { instanceUniqueId },
            },
          );
          return {
            ...r,
            _qboLinkStatus: data?.qboLinkStatus || "unmapped",
            _qboCustomerId: data?.qboCustomerId || null,
            _qboDisplayName: data?.qboDisplayName || r._qboDisplayName || null,
            _qboCustomerNumber: data?.qboCustomerNumber || r._qboCustomerNumber || null,
            _displayName: data?.name || r.customerName || "",
          };
        } catch {
          return { ...r, _qboLinkStatus: "unknown", _qboCustomerId: null };
        }
      }),
    );
    return out;
  };

  // 🎨 Local palette & button looks (no theme change)
  const UI = {
    color: {
      black: "#000",
      charcoal: "#444",
      darkRed: "#a30000",
      qbGreen: "#2ca01c",
      qbGreenHover: "#279018",
      blue: "#5757cb",
      blueHover: "#1669c1",
      teal: "#00897b",
      tealHover: "#00766a",
      amber: "#f39c12",
      amberHover: "#d98200",
      outlineHoverBg: "#f7f7f7",
    },
  };

  // base for every button (Nunito + Capitalize)
  const sxBtnBase = {
    textTransform: "capitalize",
    fontFamily: '"Nunito", sans-serif',
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 1.2,
  };

  // individual button styles
  const sxGetReport = {
    ...sxBtnBase,
    backgroundColor: UI.color.blue,
    "&:hover": { backgroundColor: UI.color.blueHover },
  };

  const openLinkAny = () => {
    setQboLinkCtx({ customerId: null, customerName: "", invoiceId: null });
    setQboLinkOpen(true);
  };

  const sxExportSelected = (enabled) =>
    enabled
      ? {
          ...sxBtnBase,
          backgroundColor: UI.color.qbGreen,
          "&:hover": { backgroundColor: UI.color.qbGreenHover },
        }
      : sxBtnBase;

  const sxUnexport = {
    ...sxBtnBase,
    color: UI.color.amber,
    borderColor: UI.color.amber,
    "&:hover": {
      color: UI.color.amberHover,
      borderColor: UI.color.amberHover,
      background: UI.color.outlineHoverBg,
    },
  };

  const sxClear = {
    ...sxBtnBase,
    color: UI.color.charcoal,
    borderColor: UI.color.charcoal,
    "&:hover": {
      color: UI.color.black,
      borderColor: UI.color.black,
      background: UI.color.outlineHoverBg,
    },
  };

  const sxDownloadCsv = {
    ...sxBtnBase,
    backgroundColor: UI.color.teal,
    "&:hover": { backgroundColor: UI.color.tealHover },
  };
  const sxImport = {
    ...sxBtnBase,
    backgroundColor: UI.color.darkRed,
    "&:hover": { backgroundColor: "#8a0000" },
  };

  const sxRefresh = {
    ...sxBtnBase,
    color: UI.color.charcoal,
    borderColor: UI.color.charcoal,
    "&:hover": {
      color: UI.color.black,
      borderColor: UI.color.black,
      background: UI.color.outlineHoverBg,
    },
  };

  const sxBackToSearch = sxClear;

  // date field: force white background to match cards
  const sxDateField = {
    "& .MuiInputBase-root": { backgroundColor: "#FFF" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#c2c2c2" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: UI.color.blue,
    },
  };

  const fetchQboStatus = async () => {
    setQbo((q) => ({ ...q, checking: true }));
    try {
      const res = await axios.get(`${SERVER}/invoices/qbo/status`, {
        ...headers,
        params: { instanceUniqueId },
      });
      setQbo({
        connected: !!res.data.connected,
        realmId: res.data.realmId || null,
        env: res.data.env || null,
        suggestedEnv: res.data.suggestedEnv || null,
        modeMismatch: !!res.data.modeMismatch,
        availableConnections: Array.isArray(res.data.availableConnections)
          ? res.data.availableConnections
          : [],
        checking: false,
      });
    } catch (e) {
      setQbo({
        connected: false,
        realmId: null,
        env: null,
        suggestedEnv: null,
        modeMismatch: false,
        availableConnections: [],
        checking: false,
      });
      console.error("QBO status error", e);
    }
  };

  const fetchQboSettingsFlag = async () => {
    try {
      const res = await axios.get(`${SERVER}/invoices/qbo/settings`, {
        ...headers,
        params: { instanceUniqueId },
      });
      const nextDepositMode = res?.data?.invoice_deposit_mode || "consolidation";
      setQboExportEnabled(!!res?.data?.qbo_export_enabled);
      setInvoiceDepositMode(nextDepositMode);
      return nextDepositMode;
    } catch (e) {
      console.error("QBO settings load error", e?.response?.data || e);
      setQboExportEnabled(false);
      setInvoiceDepositMode("consolidation");
      return "consolidation";
    }
  };

  const handleQboConnect = async () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!SERVER) return alert("Frontend SERVER env is missing.");
    if (!instanceUniqueId)
      return alert("Missing instanceUniqueId (route param).");
    try {
      const res = await axios.get(`${SERVER}/invoices/qbo/connect`, {
        headers: { accessToken },
        params: { instanceUniqueId },
      });
      const url = res.data?.url;
      if (!url) return alert(`Server did not return OAuth URL.`);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error("QBO connect error", e?.response?.data || e);
      alert(
        `Could not start QuickBooks connection.\n${
          e?.response?.data?.error || e.message
        }`,
      );
    }
  };

  const switchQboMode = async (nextEnv) => {
    const accessToken = localStorage.getItem("accessToken");
    const env = String(nextEnv || "").toLowerCase();
    if (!["sandbox", "production"].includes(env)) return;

    try {
      await axios.put(
        `${SERVER}/invoices/qbo/settings`,
        { instanceUniqueId, qbo_env: env },
        { headers: { accessToken } },
      );
      await fetchQboStatus();
    } catch (e) {
      console.error("QBO mode switch error", e?.response?.data || e);
      alert(`Could not switch QuickBooks mode.
${e?.response?.data?.error || e.message}`);
    }
  };

  const fetchAudit = async () => {
    setAuditLoading(true);
    try {
      const res = await axios.get(`${SERVER}/qbo-audit`, {
        ...headers,
        params: { entity: "invoice", instanceUniqueId, limit: 50 },
      });
      setAuditRows(res.data?.results || []);
    } catch (e) {
      console.error("Audit load failed", e);
      setAuditRows([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const fetchReport = async (overrides = {}) => {
    setLoading(true);
    try {
      const orUndef = (v) => (v ? v : undefined); // '' -> undefined

      const effectiveDateFrom = overrides.dateFrom ?? orUndef(dateFrom);
      const effectiveDateTo = overrides.dateTo ?? orUndef(dateTo);
      const effectiveView = overrides.view ?? view;

      const params = {
        instanceUniqueId,
        view: effectiveView === VIEW.DEPOSITS ? VIEW.INVOICES : effectiveView,
        dateFrom: effectiveDateFrom,
        dateTo: effectiveDateTo,
        page: 1,
        rpp: "all",
        sort: effectiveView === "exported" ? "exported_at" : "invoiceDate",
        dir: "DESC",
      };

      const list = await fetchFinanceInvoices(params);
      const visibleInvoices = filterInvoicesForFinanceView(list, effectiveView);
      const hydrated = await hydrateQboLinkStatus(visibleInvoices);
      setRows(hydrated);
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // when view changes, *pass* the cleared values:
  useEffect(() => {
    if (!instanceUniqueId) return;
    if (view === VIEW.LOGS) return;
    setSelected([]);
    setDateFrom("");
    setDateTo("");
    setStatusFilter(FINANCE_INVOICE_STATUS.ALL);
    fetchReport({ view, dateFrom: "", dateTo: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, instanceUniqueId]);

  // re-run the query whenever the Show view changes
  useEffect(() => {
    if (!instanceUniqueId) return;
    if (view === VIEW.LOGS) return;
    // optional: clear selection when switching views
    setSelected([]);
    // optional: clear date filters when switching views
    setDateFrom("");
    setDateTo("");
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, instanceUniqueId]);

  useEffect(() => {
    if (!instanceUniqueId) return;
    fetchQboStatus();
    fetchQboSettingsFlag().then((mode) => {
      if (view !== VIEW.LOGS) fetchReport({ invoiceDepositMode: mode || "consolidation" });
    });
    fetchAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  const goAttachPO = (row) => {
    if (!row?.orderId || !row?.id) return;
    const url = `/instance/${instanceUniqueId}/sales/orders/${row.orderId}/invoice/${row.id}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  
  // 🔎 helper to open QBO link dialog for a given invoice row
  const openLinkForInvoice = async (row) => {
    try {
      console.log("The row is: " + JSON.stringify(row));
      //alert(JSON.stringify(row))

      const { data } = await axios.get(
        `${SERVER}/invoices/${row.id}/customer-summary`,
        {
          ...headers,
          params: { instanceUniqueId },
        },
      );
      //alert(JSON.stringify(data))
      // ✅ correct
      if (!data) {
        return alert("Could not locate the Customer for this invoice.");
      }
      setQboLinkCtx({
        customerId: data.customerId,
        customerName: data.name || row.customerName || "",
        invoiceId: row.id,
      });
      setQboLinkOpen(true);
    } catch (e) {
      console.error(e);
      alert("Failed to load related customer.");
    }
  };

  // ✅ toolbar helper: open link dialog using single selected invoice
  const openLinkForSelected = async () => {
    if (selected.length !== 1) {
      alert("Select exactly one invoice to manage its customer link.");
      return;
    }
    const row = rows.find((r) => String(r.id) === String(selected[0]));
    if (!row) return alert("Could not find that invoice in the current table.");
    await openLinkForInvoice(row);
  };

  const removeRowsById = (idsToRemove = []) => {
    const removeSet = new Set((idsToRemove || []).map((id) => String(id)));
    if (!removeSet.size) return;
    setRows((prev) => {
      return prev.filter((r) => !removeSet.has(String(r?.id)));
    });
    setSelected((prev) => prev.filter((id) => !removeSet.has(String(id))));
  };

  const exportOne = async (row, opts = {}) => {
    const { skipAudit = false, skipSpinner = false } = opts;
    if (!qboExportEnabled)
      return alert("Enable QBO Exports in Integrations first.");
    if (!qboExportEnabled)
      return alert("Enable QBO Exports in Integrations first.");
    if (!qbo.connected) return alert("Connect QuickBooks first.");
    if (isDepositInvoiceRole(row)) {
      return alert(
        invoiceDepositMode === "consolidation"
          ? getDepositConsolidationHint()
          : "Deposit invoices are not exported to QBO. Export a partial, balance, or full invoice instead.",
      );
    }
    if (!isQboCustomerLinked(row)) {
      await openLinkForInvoice(row);
      return;
    }
    try {
      if (!skipSpinner) setExportingId(row.id);
      const res = await axios.post(
        `${SERVER}/invoices/${row.id}/export/qbo`,
        { instanceUniqueId },
        headers,
      );
      const { id, docNumber } = res.data || {};
      if (view === VIEW.INVOICES) {
        removeRowsById([row.id]);
      } else {
        setRows((prev) =>
          prev.map((r) =>
            String(r?.id) === String(row?.id)
              ? {
                  ...r,
                  qboExportStatus: "exported",
                  qboInvoiceId: id,
                  qboDocNumber: docNumber,
                  qboLastSyncAt: new Date().toISOString(),
                  exported_at: new Date().toISOString(),
                  qboSyncError: null,
                  _qboLinkStatus: r._qboLinkStatus,
                }
              : r,
          ),
        );
      }
      if (!skipAudit) fetchAudit();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        "Export failed. Check customer is linked in QBO.";
      //alert(msg);
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id
            ? {
                ...r,
                qboExportStatus: "failed",
                qboSyncError: msg,
                qboLastSyncAt: new Date().toISOString(),
              }
            : r,
        ),
      );

      // 🟡 if it's the mapping issue → offer to link immediately
      const upper = String(msg || "").toUpperCase();
      if (
        upper.includes("CUSTOMER NOT LINKED") ||
        upper.includes("CUSTOMER_UNMAPPED")
      ) {
        openLinkForInvoice(row);
      }

      if (!skipAudit) fetchAudit();
    } finally {
      if (!skipSpinner) setExportingId(null);
    }
  };

  const exportSelected = async () => {
    if (!qbo.connected) return alert("Connect QuickBooks first.");
    const confirmed = window.confirm("This will send selected records to QuickBooks and mark them Exported.");
    if (!confirmed) return;
    const map = new Map(rows.map((r) => [r.id, r]));
    const candidates = selected
      .map((id) => map.get(id))
      .filter(
        (r) => r && !isInactiveInvoice(r) && r.qboExportStatus !== "exported" && !isDepositInvoiceRole(r),
      );
    const linkedRows = candidates.filter((row) => isQboCustomerLinked(row));
    const unmappedRow = candidates.find((row) => !isQboCustomerLinked(row));
    if (unmappedRow) {
      await openLinkForInvoice(unmappedRow);
    }
    const attemptedIds = linkedRows.map((r) => r.id);

    const concurrency = 3;
    let index = 0;
    const worker = async () => {
      while (index < linkedRows.length) {
        const current = linkedRows[index++];
        // eslint-disable-next-line no-await-in-loop
        await exportOne(current, { skipAudit: true, skipSpinner: true });
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(concurrency, linkedRows.length) }, () =>
        worker(),
      ),
    );
    setSelected((prev) =>
      prev.filter((id) => !attemptedIds.some((x) => String(x) === String(id))),
    );
    await fetchReport({ view });
    fetchAudit();
  };

  const downloadFromResponse = async (res, fallbackName) => {
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const runInvoiceFileExport = async (method = "csv") => {
    if (!selected.length) return;
    const confirmed = window.confirm("This will generate a file and mark selected records as Exported.");
    if (!confirmed) return;
    const res = await fetch(`${SERVER}/invoices/export/${method}`, {
      method: "POST",
      headers: { accessToken: localStorage.getItem("accessToken"), "Content-Type": "application/json" },
      body: JSON.stringify({ instanceUniqueId, ids: selected, markExported: true }),
    });
    if (!res.ok) return alert(`Export failed: ${await res.text()}`);
    await downloadFromResponse(res, `invoices-export-${new Date().toISOString().slice(0, 10)}.${method}`);
    await fetchReport();
    fetchAudit();
  };

  const runInvoiceReportDownload = async (method = "csv") => {
    const params = new URLSearchParams({ instanceUniqueId });
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);
    const res = await fetch(`${SERVER}/invoices/report/${method}?${params.toString()}`, { headers: { accessToken: localStorage.getItem("accessToken") } });
    if (!res.ok) return alert(`Download failed: ${await res.text()}`);
    await downloadFromResponse(res, `invoices-report-${new Date().toISOString().slice(0, 10)}.${method}`);
    fetchAudit();
  };

  const bulkUnexportSelected = async () => {
    if (!selected.length) return;
    const confirmed = window.confirm("This resets export status in Elite Core only. It does not remove or reverse records in QuickBooks or external accounting systems.");
    if (!confirmed) return;
    const reason = window.prompt("Reason for unexport (required):", "");
    if (!String(reason || "").trim()) return alert("Reason is required.");
    await axios.post(`${SERVER}/invoices/unexport`, { ids: selected, reason: String(reason).trim() }, headers);
    await fetchReport();
    fetchAudit();
    setSelected([]);
  };

  const applyQboPaymentSyncResult = (result) => {
    if (!result?.invoiceId) return;
    setRows((prev) =>
      prev.map((r) =>
        String(r.id) === String(result.invoiceId)
          ? {
              ...r,
              accountingPaymentStatus: result.accountingPaymentStatus,
              accountingPaidAmount: result.accountingPaidAmount,
              accountingBalance: result.accountingBalance,
              accountingTotalAmount: result.accountingTotalAmount,
              accountingPaymentLastSyncAt: result.accountingPaymentLastSyncAt,
              accountingPaymentSyncError: result.needsReview ? result.message : null,
              qboDocNumber: result.qboDocNumber || r.qboDocNumber,
              ...(result.accountingPaymentStatus === "paid"
                ? { paymentStatus: "Paid", invoiceStatus: "Completed", isActive: false }
                : {}),
            }
          : r,
      ),
    );
  };

  const refreshFromQbo = async (row) => {
    setQboPaymentSyncingId(row.id);
    try {
      const { data } = await axios.post(
        `${SERVER}/invoices/${row.id}/qbo/sync-payment-status`,
        { instanceUniqueId },
        headers,
      );
      applyQboPaymentSyncResult(data);
      alert(data?.message || "QBO payment status synced.");
      await fetchReport();
      fetchAudit();
    } catch (e) {
      console.error("QBO payment status sync failed", e?.response?.data || e);
      alert(e?.response?.data?.error || "QBO payment status sync failed");
    } finally {
      setQboPaymentSyncingId(null);
    }
  };

  const syncAllQboInvoicePayments = async () => {
    setQboPaymentBatchSyncing(true);
    try {
      const { data } = await axios.post(
        `${SERVER}/invoices/qbo/sync-payment-statuses`,
        { instanceUniqueId, limit: 50 },
        headers,
      );
      (data?.results || []).forEach(applyQboPaymentSyncResult);
      alert(`QBO payment sync complete. Synced: ${data?.synced || 0}, skipped: ${data?.skipped || 0}, failed: ${data?.failed || 0}, needs review: ${data?.needsReview || 0}.`);
      await fetchReport();
      fetchAudit();
    } catch (e) {
      console.error("Batch QBO payment status sync failed", e?.response?.data || e);
      alert(e?.response?.data?.error || "Batch QBO payment status sync failed");
    } finally {
      setQboPaymentBatchSyncing(false);
    }
  };

  const statusChip = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "exported")
      return <Chip size="small" label="Exported" color="success" />;
    if (s === "failed")
      return <Chip size="small" label="Failed" color="error" />;
    return <Chip size="small" label="Pending" />;
  };

  // ─────────────────────────────────────────────
  // Column definitions (toggleable like PO screen)
  // ─────────────────────────────────────────────
  const actionColInvoices = {
    field: "actions",
    headerName: "Actions",
    width: 420,
    sortable: false,
    renderCell: ({ row }) => {
      const isLinked = isQboCustomerLinked(row);
      const isDepositInvoice = isDepositInvoiceRole(row);
      const depositExportHint = invoiceDepositMode === "consolidation"
        ? getDepositConsolidationHint()
        : "Deposit invoices are not exported to QBO. Export a partial, balance, or full invoice instead.";
      const disabled =
        !qboExportEnabled ||
        !qbo.connected ||
        isDepositInvoice ||
        (row.qboExportStatus === "exported" && exportingId === row.id);
      const label = !qboExportEnabled
        ? "QBO exports disabled"
        : !qbo.connected
        ? "Connect QBO"
        : isDepositInvoice
        ? "Deposit (Blocked)"
        : row.qboExportStatus === "exported"
        ? "Exported"
        : "Export to QBO";
      const connectedGreen =
        qboExportEnabled && qbo.connected && !isDepositInvoice && row.qboExportStatus !== "exported";
      const showLinkBtn =
        !isLinked &&
        row.qboSyncError &&
        /CUSTOMER\s*NOT\s*LINKED|CUSTOMER_UNMAPPED/i.test(
          String(row.qboSyncError),
        );

      return (
        <Box
          sx={{ display: "flex", gap: 1, alignItems: "center" }}
          className="table-row-actions"
        >
          <Tooltip title={isDepositInvoice ? depositExportHint : ""}>
            <span>
              <Button
                size="small"
                variant="contained"
                disabled={disabled || row.qboExportStatus === "exported"}
                onClick={() => exportOne(row)}
                startIcon={
                  <img src={QB_ICON} alt="" style={{ width: 16, height: 16 }} />
                }
                sx={{
                  ...btnSx,
                  ...(connectedGreen
                    ? {
                        backgroundColor: "#2ca01c",
                        "&:hover": { backgroundColor: "#279018" },
                      }
                    : {}),
                }}
              >
                {exportingId === row.id ? <CircularProgress size={18} /> : label}
              </Button>
            </span>
          </Tooltip>

          {row.qboExportStatus === "exported" && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => refreshFromQbo(row)}
              disabled={qboPaymentSyncingId === row.id}
              sx={btnSx}
            >
              {qboPaymentSyncingId === row.id ? <CircularProgress size={16} /> : "Sync QBO Payment Status"}
            </Button>
          )}

          {/* quick “link” CTA if unmapped */}
          {showLinkBtn && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => openLinkForInvoice(row)}
              sx={btnSx}
            >
              Link QBO
            </Button>
          )}

          {/* show latest error inline */}
          {row.qboSyncError && (
            <Tooltip title={row.qboSyncError}>
              <Chip size="small" label="Error" color="error" />
            </Tooltip>
          )}

          {/* duplicate doc helper */}
          {row.qboSyncError &&
            String(row.qboSyncError).startsWith("DUPLICATE_DOC") && (
              <Button
                size="small"
                variant="outlined"
                onClick={async () => {
                  const user = prompt(
                    "Enter a new Doc # (letters/numbers, we will prefix & clean):",
                    row.qboDocNumber || `${row.orderId}-${row.id}`,
                  );
                  if (!user) return;
                  try {
                    await axios.put(
                      `${SERVER}/invoices/${row.id}/qbo/doc-number`,
                      { docNumber: user, instanceUniqueId },
                      headers,
                    );
                    await exportOne(row);
                  } catch (e) {
                    alert(
                      e?.response?.data?.error || "Could not set Doc Number",
                    );
                  }
                }}
                sx={btnSx}
              >
                Fix Doc #
              </Button>
            )}
        </Box>
      );
    },
  };

  // Toggleable columns registry
  const COLUMN_DEFS = {
    id: {
      label: "ID",
      build: () => ({ field: "id", headerName: "ID", width: 80 }),
    },
    orderId: {
      label: "Sales Order #",
      build: () => ({
        field: "orderId",
        headerName: "Sales Order #",
        width: 140,
      }),
    },
    customerName: {
      label: "Client",
      build: () => ({
        field: "customerName",
        headerName: "Client",
        width: 260,
        sortable: true,
        renderCell: ({ row }) => {
          const title = row._displayName || row.customerName || "—";
          const sub = row.clientContact || "";
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: 0,
              }}
            >
              <QboDot linked={isQboCustomerLinked(row)} />
              <Box sx={{ minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {title}
                </div>
                {sub ? (
                  <div
                    style={{
                      fontSize: 12,
                      color: "#666",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                    }}
                  >
                    {sub}
                  </div>
                ) : null}
              </Box>
            </Box>
          );
        },
      }),
    },    
    invoiceStatus: {
      label: "Status",
      build: () => ({
        field: "invoiceStatus",
        headerName: "Status",
        width: 120,
      }),
    },
    invoiceDate: {
      label: "Invoice Date",
      build: () => ({
        field: "invoiceDate",
        headerName: "Invoice Date",
        width: 140,
        valueGetter: (value, row) =>
          row?.invoiceDate
            ? new Date(row.invoiceDate).toLocaleDateString()
            : "—",
      }),
    },
    amount: {
      label: "Amount",
      build: () => ({
        field: "amount",
        headerName: "Amount",
        width: 120,
        valueGetter: (value, row) => {
          const amt = parseFloat(row?.amount) || 0;
          return `$${amt.toFixed(2)} ${row?.currency || ""}`.trim();
        },
      }),
    },
    qboExportStatus: {
      label: "QBO Export",
      build: () => ({
        field: "qboExportStatus",
        headerName: "QBO Export",
        width: 140,
        renderCell: ({ row }) => statusChip(row.qboExportStatus),
      }),
    },
    qboDocNumber: {
      label: "QBO Doc #",
      build: () => ({
        field: "qboDocNumber",
        headerName: "QBO Doc #",
        width: 140,
        valueGetter: (value, row) => row?.qboDocNumber || "—",
      }),
    },
    accountingPaymentStatus: {
      label: "QBO Payment Status",
      build: () => ({
        field: "accountingPaymentStatus",
        headerName: "QBO Payment Status",
        width: 190,
        renderCell: ({ row }) => {
          const status = String(row?.accountingPaymentStatus || "unknown").toLowerCase();
          const label = status === "partially_paid"
            ? "Partially Paid"
            : status === "needs_review"
              ? "Needs Review"
              : status ? status.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) : "Unknown";
          const color = status === "paid" ? "success" : status === "needs_review" ? "warning" : status === "partially_paid" ? "info" : "default";
          const chip = <Chip size="small" label={label} color={color} />;
          return status === "needs_review" || row?.accountingPaymentSyncError
            ? <Tooltip title={row?.accountingPaymentSyncError || "Review QBO payment status."}>{chip}</Tooltip>
            : chip;
        },
      }),
    },
    accountingPaidAmount: {
      label: "QBO Paid Amount",
      build: () => ({
        field: "accountingPaidAmount",
        headerName: "QBO Paid Amount",
        width: 160,
        valueGetter: (value, row) => row?.accountingPaidAmount == null ? "—" : `$${Number(row.accountingPaidAmount || 0).toFixed(2)}`,
      }),
    },
    accountingBalance: {
      label: "QBO Balance",
      build: () => ({
        field: "accountingBalance",
        headerName: "QBO Balance",
        width: 140,
        valueGetter: (value, row) => row?.accountingBalance == null ? "—" : `$${Number(row.accountingBalance || 0).toFixed(2)}`,
      }),
    },
    accountingPaymentLastSyncAt: {
      label: "Last QBO Payment Sync",
      build: () => ({
        field: "accountingPaymentLastSyncAt",
        headerName: "Last QBO Payment Sync",
        width: 190,
        valueGetter: (value, row) => row?.accountingPaymentLastSyncAt ? new Date(row.accountingPaymentLastSyncAt).toLocaleString() : "—",
      }),
    },
    exported_at: {
      label: "Exported At",
      build: () => ({
        field: "exported_at",
        headerName: "Exported At",
        width: 180,
        valueGetter: (value, row) =>
          row?.exported_at || row?.qboLastSyncAt
            ? new Date(row?.exported_at || row?.qboLastSyncAt).toLocaleString()
            : "—",
      }),
    },
    exportMethod: {
      label: "Export Method",
      build: () => ({ field: "exportMethod", headerName: "Export Method", width: 140, valueGetter: (value, row) => row?.exportMethod || "—" }),
    },
    exportBatchId: {
      label: "Export Batch",
      build: () => ({ field: "exportBatchId", headerName: "Export Batch", width: 220, valueGetter: (value, row) => row?.exportBatchId || "—" }),
    },
    exportedById: {
      label: "Exported By",
      build: () => ({ field: "exportedById", headerName: "Exported By", width: 120, valueGetter: (value, row) => row?.exportedById || "—" }),
    },
    qboCustomerLink: {
      label: "QBO Customer",
      build: () => ({
        field: "_qboLinkStatus",
        headerName: "QBO Customer",
        width: 160,
        renderCell: ({ row }) => {
          const s = String(row._qboLinkStatus || "").toLowerCase();
          if (hasStoredQboCustomerLink(row)) {
            return (
              <Chip
                size="small"
                icon={<CheckCircleIcon fontSize="small" />}
                color="success"
                label={`Linked #${row._qboCustomerId || ""}`.trim()}
              />
            );
          }
          if (s === "unmapped") {
            return (
              <Chip
                size="small"
                icon={<ErrorOutlineIcon fontSize="small" />}
                label="Not linked"
                onClick={() => openLinkForInvoice(row)}
                variant="outlined"
              />
            );
          }
          return <Chip size="small" label="—" />;
        },
      }),
    },

    unexportAdmin: {
      label: "Administrator",
      build: () => ({
        field: "unexportAdmin",
        headerName: "Administrator",
        width: 150,
        sortable: false,
        renderCell: ({ row }) => {
          if (!isAdministrator) return <span>—</span>;
          return (
            <Button
              size="small"
              variant="outlined"
              sx={sxUnexport}
              onClick={async () => {
                try {
                  await axios.post(
                    `${SERVER}/invoices/unexport`,
                    { ids: [row.id] },
                    headers,
                  );
                  await fetchReport();
                  fetchAudit();
                } catch (e) {
                  console.error(e);
                  alert(e?.response?.data?.error || "Unexport failed");
                }
              }}
            >
              Unexport
            </Button>
          );
        },
      }),
    },
  };

  // Default visible sets (per view)
  const DEFAULT_VISIBLE = {
    [VIEW.INVOICES]: [
      "id",
      "orderId",
      "invoiceType",
      "customerName",
      "invoiceStatus",
      "invoiceDate",
      "amount",
      "qboExportStatus",
      "qboDocNumber",
      "accountingPaymentStatus",
      "accountingPaidAmount",
      "accountingBalance",
      "accountingPaymentLastSyncAt",
      "exported_at",
      "exportMethod",
      "exportBatchId",
      "exportedById",
      "actions",
    ],
    [VIEW.EXPORTED]: [
      "id",
      "orderId",
      "invoiceType",
      "customerName",
      "invoiceDate",
      "amount",
      "qboExportStatus",
      "qboDocNumber",
      "accountingPaymentStatus",
      "accountingPaidAmount",
      "accountingBalance",
      "accountingPaymentLastSyncAt",
      "exported_at",
      "exportMethod",
      "exportBatchId",
      "exportedById",
      "unexportAdmin",
    ],
    [VIEW.DEPOSITS]: [
      "id",
      "depositActions",
      "orderId",
      "invoiceType",
      "customerName",
      "invoiceStatus",
      "invoiceDate",
      "amount",
    ],
    [VIEW.LOGS]: [],
  };

  // Persist column visibility per instance + view
  const columnsStorageKey = (id, v) => `invoicing.columns.${id}.${v}`;
  const [visibleCols, setVisibleCols] = useState(
    DEFAULT_VISIBLE[VIEW.INVOICES],
  );
  const [colMenuEl, setColMenuEl] = useState(null);
  const openColMenu = Boolean(colMenuEl);

  // load on mount / when view changes
  useEffect(() => {
    const key = columnsStorageKey(instanceUniqueId, view);
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const arr = JSON.parse(saved);
        if (Array.isArray(arr) && arr.length) {
          setVisibleCols(arr.filter((columnId) => columnId !== "customerPO"));
          return;
        }
      } catch {}
    }
    setVisibleCols(DEFAULT_VISIBLE[view]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId, view]);

  // persist on change
  useEffect(() => {
    const key = columnsStorageKey(instanceUniqueId, view);
    try {
      localStorage.setItem(key, JSON.stringify(visibleCols));
    } catch {}
  }, [visibleCols, instanceUniqueId, view]);

  const toggleCol = (key) =>
    setVisibleCols((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  // const selectAllCols = () => setVisibleCols(Object.keys(COLUMN_DEFS));
  // const clearAllCols = () => setVisibleCols([]);

  const selectAllCols = () =>
    setVisibleCols(ALL_INVOICING_COLUMNS.map((c) => c.id));
  const clearAllCols = () =>
    setVisibleCols(
      ALL_INVOICING_COLUMNS.filter((c) => c.locked).map((c) => c.id),
    );

  // Final columns (Actions column is always appended & visible)
  const columns = useMemo(() => {
    const built = (visibleCols || [])
      .map((key) => COLUMN_DEFS[key]?.build())
      .filter(Boolean);
    if (view === VIEW.INVOICES) return [...built, actionColInvoices]; // actions only on active Invoices
    return built;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleCols, view, qbo.connected, exportingId, rows]);

  const ALL_INVOICING_COLUMNS = [
    { id: "depositActions", label: "View / Print", sortable: false, minWidth: 190 },
    { id: "id", label: "ID", sortable: false, minWidth: 80 },
    { id: "orderId", label: "Sales Order #", sortable: true, minWidth: 130 },
    { id: "invoiceType", label: "Invoice Type", sortable: false, minWidth: 170 },
    { id: "customerName", label: "Client", sortable: true, minWidth: 220 },
    { id: "invoiceStatus", label: "Status", sortable: false, minWidth: 120 },
    { id: "invoiceDate", label: "Invoice Date", sortable: true, minWidth: 140 },
    { id: "amount", label: "Amount", sortable: true, minWidth: 120 },
    {
      id: "qboExportStatus",
      label: "QBO Export",
      sortable: false,
      minWidth: 140,
    },
    { id: "qboDocNumber", label: "QBO Doc #", sortable: false, minWidth: 140 },
    { id: "accountingPaymentStatus", label: "QBO Payment Status", sortable: false, minWidth: 190 },
    { id: "accountingPaidAmount", label: "QBO Paid Amount", sortable: false, minWidth: 160 },
    { id: "accountingBalance", label: "QBO Balance", sortable: false, minWidth: 140 },
    { id: "accountingPaymentLastSyncAt", label: "Last QBO Payment Sync", sortable: false, minWidth: 190 },
    { id: "exported_at", label: "Exported At", sortable: true, minWidth: 180 },
    { id: "exportMethod", label: "Export Method", sortable: false, minWidth: 140 },
    { id: "exportBatchId", label: "Export Batch", sortable: false, minWidth: 220 },
    { id: "exportedById", label: "Exported By", sortable: false, minWidth: 120 },
    {
      id: "qboCustomerLink",
      label: "QBO Customer",
      sortable: false,
      minWidth: 160,
    },
    {
      id: "unexportAdmin",
      label: "Administrator",
      sortable: false,
      minWidth: 150,
    },
    {
      id: "actions",
      label: "Actions",
      sortable: false,
      minWidth: 380,
      locked: true,
    },
  ];

  const handleSort = (colId) => {
    if (sortBy === colId) {
      if (sortDir === "asc") setSortDir("desc");
      else {
        setSortBy("");
        setSortDir("asc");
      }
    } else {
      setSortBy(colId);
      setSortDir("asc");
    }
  };

  const statusFilteredRows = useMemo(
    () => filterFinanceInvoicesByStatus(rows, statusFilter),
    [rows, statusFilter],
  );

  const visibleSummary = useMemo(() => ({
    count: statusFilteredRows.length,
    total: statusFilteredRows.reduce((sum, invoice) => sum + (parseFloat(invoice?.amount) || 0), 0),
  }), [statusFilteredRows]);

  const sortedRows = useMemo(() => {
    if (!sortBy) return statusFilteredRows;
    return [...statusFilteredRows].sort((a, b) => {
      if (sortBy === "amount") {
        const av = Number(a.amount || 0),
          bv = Number(b.amount || 0);
        return sortDir === "asc" ? av - bv : bv - av;
      }
      const av = String(a[sortBy] || "");
      const bv = String(b[sortBy] || "");
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [statusFilteredRows, sortBy, sortDir]);

  const pagedRows = useMemo(
    () =>
      sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage],
  );

  const renderCell = (colId, row) => {
    const { invoiceUrl, salesOrderUrl } = buildFinanceInvoicingRowLinks(instanceUniqueId, row);
    switch (colId) {
      case "depositActions":
        return invoiceUrl ? (
          <Stack direction="row" spacing={0.5}>
            <Button component="a" href={invoiceUrl} target="_blank" rel="noopener noreferrer" size="small" variant="outlined" sx={{ textTransform: "none" }}>View invoice</Button>
            <Tooltip title="Open invoice with its Print / PDF control"><Button component="a" href={invoiceUrl} target="_blank" rel="noopener noreferrer" size="small" sx={{ textTransform: "none" }}>Print / PDF</Button></Tooltip>
          </Stack>
        ) : <Typography variant="caption">Unavailable</Typography>;
      case "id":
        return invoiceUrl ? (
          <Button
            component="a"
            href={invoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="text"
            size="small"
            sx={{ textTransform: "none", minWidth: 0, px: 0.5, fontFamily: "monospace", fontSize: "0.82rem", fontWeight: 700 }}
          >
            {isDepositInvoiceRole(row) ? `DEP #${row.id}` : `INV #${row.id}`}
          </Button>
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.82rem",
              color: "primary.main",
              fontWeight: 700,
            }}
          >
            {isDepositInvoiceRole(row) ? `DEP #${row.id}` : `INV #${row.id}`}
          </Typography>
        );
      case "orderId":
        return salesOrderUrl ? (
          <Button
            component="a"
            href={salesOrderUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="text"
            size="small"
            endIcon={<OpenInNewIcon aria-hidden="true" sx={{ fontSize: "0.9rem !important" }} />}
            sx={{ textTransform: "none", minWidth: 0, px: 0.5, fontWeight: 700, gap: 0.25 }}
          >
            {row.orderId || "—"}
          </Button>
        ) : (
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: C.textPri }}
          >
            {row.orderId || "—"}
          </Typography>
        );
      case "invoiceType": {
        const tone = isDepositInvoiceRole(row) ? "warning" : "neutral";
        const showDepositAttached = hasDepositAttachment(row);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            <UIStatusChip label={getInvoiceTypeLabel(row)} tone={tone} />
            {showDepositAttached && (
              <Tooltip title={getDepositAttachedTooltip(row)}>
                <Chip
                  size="small"
                  variant="outlined"
                  color="info"
                  icon={<ReceiptLongIcon fontSize="small" />}
                  label="Deposit attached"
                />
              </Tooltip>
            )}
          </Box>
        );
      }
      case "customerName": {
        const title = row._displayName || row.customerName || "—";
        const sub = row.clientContact || "";
        return (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
          >
            <QboDot linked={isQboCustomerLinked(row)} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  color: C.textPri,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </Typography>
              {sub && (
                <Typography
                  variant="caption"
                  sx={{ color: C.textMuted, fontSize: "0.72rem" }}
                >
                  {sub}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }
      case "invoiceStatus": {
        const s = String(row.invoiceStatus || "").toLowerCase();
        const tone =
          s === "completed"
            ? "success"
            : s === "cancelled"
            ? "error"
            : "neutral";
        return <UIStatusChip label={row.invoiceStatus || "—"} tone={tone} />;
      }
      case "invoiceDate":
        return (
          <Typography variant="body2" sx={{ color: C.textSec }}>
            {row.invoiceDate
              ? new Date(row.invoiceDate).toLocaleDateString()
              : "—"}
          </Typography>
        );
      case "amount": {
        const amt = parseFloat(row?.amount) || 0;
        const qboIndicator = getQboAmountIndicator(row, invoiceDepositMode);
        const qboChip =
          qboIndicator.tone === "warning"
            ? { color: "warning.main", borderColor: "warning.main" }
            : qboIndicator.tone === "success"
              ? { color: "success.main", borderColor: "success.main" }
              : { color: "info.main", borderColor: "info.main" };

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{ color: C.textPri, fontWeight: 600, fontFamily: "monospace", whiteSpace: "nowrap" }}
            >
              ${amt.toFixed(2)} {row?.currency || ""}
            </Typography>
            {qboIndicator.show ? (
              <Tooltip title={qboIndicator.title} arrow placement="top">
                <Chip
                  size="small"
                  variant="outlined"
                  label={qboIndicator.label}
                  sx={{
                    height: 18,
                    minWidth: 0,
                    fontSize: "0.62rem",
                    fontWeight: 800,
                    px: 0,
                    "& .MuiChip-label": { px: 0.6 },
                    ...qboChip,
                  }}
                />
              </Tooltip>
            ) : null}
          </Box>
        );
      }
      case "qboExportStatus": {
        const s = String(row.qboExportStatus || "").toLowerCase();
        const tone =
          s === "exported" ? "success" : s === "failed" ? "error" : "neutral";
        const label =
          s === "exported" ? "Exported" : s === "failed" ? "Failed" : "Pending";
        return <UIStatusChip label={label} tone={tone} />;
      }
      case "qboDocNumber":
        return (
          <Typography
            variant="body2"
            sx={{ color: C.textSec, fontFamily: "monospace" }}
          >
            {row.qboDocNumber || "—"}
          </Typography>
        );
      case "accountingPaymentStatus": {
        const status = String(row.accountingPaymentStatus || "unknown").toLowerCase();
        const label = status === "partially_paid"
          ? "Partially Paid"
          : status === "needs_review"
            ? "Needs Review"
            : status.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
        const tone = status === "paid" ? "success" : status === "needs_review" ? "warning" : status === "partially_paid" ? "info" : "neutral";
        const chip = <UIStatusChip label={label || "Unknown"} tone={tone} />;
        return status === "needs_review" || row.accountingPaymentSyncError
          ? <Tooltip title={row.accountingPaymentSyncError || "Review QBO payment status."}>{chip}</Tooltip>
          : chip;
      }
      case "accountingPaidAmount":
        return <Typography variant="body2" sx={{ color: C.textSec }}>{row.accountingPaidAmount == null ? "—" : `$${Number(row.accountingPaidAmount || 0).toFixed(2)}`}</Typography>;
      case "accountingBalance":
        return <Typography variant="body2" sx={{ color: C.textSec }}>{row.accountingBalance == null ? "—" : `$${Number(row.accountingBalance || 0).toFixed(2)}`}</Typography>;
      case "accountingPaymentLastSyncAt":
        return <Typography variant="body2" sx={{ color: C.textSec }}>{row.accountingPaymentLastSyncAt ? new Date(row.accountingPaymentLastSyncAt).toLocaleString() : "—"}</Typography>;
      case "exported_at": {
        const d = row.exported_at || row.qboLastSyncAt;
        return (
          <Typography variant="body2" sx={{ color: C.textSec }}>
            {d ? new Date(d).toLocaleString() : "—"}
          </Typography>
        );
      }
      case "qboCustomerLink": {
        const s = String(row._qboLinkStatus || "").toLowerCase();
        if (hasStoredQboCustomerLink(row))
          return (
            <UIStatusChip
              label={`Linked #${row._qboCustomerId || ""}`.trim()}
              tone="success"
            />
          );
        if (s === "unmapped")
          return (
            <Chip
              size="small"
              icon={<ErrorOutlineIcon fontSize="small" />}
              label="Not linked"
              onClick={() => openLinkForInvoice(row)}
              variant="outlined"
            />
          );
        return (
          <Typography variant="body2" sx={{ color: C.textMuted }}>
            —
          </Typography>
        );
      }
      case "unexportAdmin":
        if (!isAdministrator)
          return (
            <Typography variant="body2" sx={{ color: C.textMuted }}>
              —
            </Typography>
          );
        return (
          <Button
            size="small"
            variant="outlined"
            onClick={async () => {
              try {
                await axios.post(
                  `${SERVER}/invoices/unexport`,
                  { ids: [row.id] },
                  headers,
                );
                await fetchReport();
                fetchAudit();
              } catch (e) {
                alert(e?.response?.data?.error || "Unexport failed");
              }
            }}
            sx={{
              fontWeight: 600,
              textTransform: "none",
              height: 28,
              px: 1.5,
              fontSize: "0.75rem",
              borderRadius: "8px",
              color: "warning.main",
              borderColor: "warning.main",
            }}
          >
            Unexport
          </Button>
        );
      case "actions": {
        const alreadyExported = row.qboExportStatus === "exported";
        const isDepositInvoice = isDepositInvoiceRole(row);
        const canExport = qboExportEnabled && qbo.connected && !alreadyExported && !isDepositInvoice;
        const label = !qboExportEnabled
          ? "Exports disabled"
          : !qbo.connected
          ? "Connect QBO"
          : isDepositInvoice
          ? "Deposit (Blocked)"
          : alreadyExported
          ? "Exported"
          : "Export to QBO";
        const depositExportHint = invoiceDepositMode === "consolidation"
          ? getDepositConsolidationHint()
          : "Deposit invoices are not exported to QBO. Export a partial, balance, or full invoice instead.";
        const showLinkBtn =
          !isQboCustomerLinked(row) &&
          row.qboSyncError &&
          /CUSTOMER\s*NOT\s*LINKED|CUSTOMER_UNMAPPED/i.test(
            String(row.qboSyncError),
          );
        return (
          <UITableActionCell align="flex-start">
            <Tooltip title={isDepositInvoice ? depositExportHint : ""}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!canExport}
                  onClick={() => exportOne(row)}
                  startIcon={
                    exportingId === row.id ? null : (
                      <img src={QB_ICON} alt="" style={{ width: 14, height: 14 }} />
                    )
                  }
                  sx={{
                    fontWeight: 700,
                    textTransform: "none",
                    height: 28,
                    px: 1.5,
                    fontSize: "0.75rem",
                    borderRadius: "8px",
                    boxShadow: "none",
                    ...(canExport
                      ? {
                          backgroundColor: "#2ca01c",
                          "&:hover": {
                            backgroundColor: "#279018",
                            boxShadow: "none",
                          },
                        }
                      : {}),
                  }}
                >
                  {exportingId === row.id ? (
                    <CircularProgress size={14} sx={{ color: "#fff" }} />
                  ) : (
                    label
                  )}
                </Button>
              </span>
            </Tooltip>
            {alreadyExported && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => refreshFromQbo(row)}
                disabled={qboPaymentSyncingId === row.id}
                sx={{
                  fontWeight: 600,
                  textTransform: "none",
                  height: 28,
                  px: 1.5,
                  fontSize: "0.75rem",
                  borderRadius: "8px",
                }}
              >
                {qboPaymentSyncingId === row.id ? <CircularProgress size={14} /> : "Sync QBO Payment Status"}
              </Button>
            )}
            {showLinkBtn && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => openLinkForInvoice(row)}
                sx={{
                  fontWeight: 600,
                  textTransform: "none",
                  height: 28,
                  px: 1.5,
                  fontSize: "0.75rem",
                  borderRadius: "8px",
                }}
              >
                Link QBO
              </Button>
            )}
            {row.qboSyncError && (
              <Tooltip title={row.qboSyncError}>
                <span>
                  <UIStatusChip label="Error" tone="error" />
                </span>
              </Tooltip>
            )}
          </UITableActionCell>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="instance-dashboard instance-orders-page">
      <InstanceLeftSidebar />
      <div className="rightSide">
        <Box
          sx={{
            p: { xs: 1, md: 2 },
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 60px)",
            overflow: "hidden",
          }}
        >
          <UITableShell
            customLayout
            paperSx={{
              flex: 1,
              borderRadius: "10px",
              border: "1px solid",
              borderColor: "divider",
              borderTop: "3px solid",
              borderTopColor: "primary.main",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 1px 3px 0 rgba(0,0,0,0.07)",
            }}
          >
            <UITableToolbar sx={{ backgroundColor: C.paperBg, flexShrink: 0 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1.5}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: C.textPri, lineHeight: 1 }}
                  >
                    Invoicing
                  </Typography>

                  <Chip
                    label={`${visibleSummary.count} total`}
                    size="small"
                    sx={{
                      backgroundColor: "var(--ec-status-neutral-bg)",
                      color: "var(--ec-status-neutral-text)",
                      border: "1px solid var(--ec-status-neutral-border)",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                    }}
                  />

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ height: 20, alignSelf: "center" }}
                  />

                  <Stack direction="row" spacing={0.5}>
                    {[VIEW.INVOICES, VIEW.DEPOSITS, VIEW.EXPORTED, VIEW.LOGS].map((v) => (
                      // <Chip
                      //   key={v}
                      //   label={
                      //     v === VIEW.INVOICES ? "Invoices" : "Exported Invoices"
                      //   }
                      //   size="small"
                      //   onClick={() => setView(v)}
                      <Chip
                        key={v}
                        label={
                          v === VIEW.INVOICES
                            ? "Invoices"
                            : v === VIEW.DEPOSITS
                            ? "Deposits"
                            : v === VIEW.EXPORTED
                            ? "Exported Invoices"
                            : "Last Exports"
                        }
                        size="small"
                        onClick={() => {
                          setView(v);
                          if (v === VIEW.LOGS) fetchAudit();
                        }}
                        sx={{
                          cursor: "pointer",
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          height: 26,
                          backgroundColor:
                            view === v
                              ? "var(--ec-color-primary, #1976d2)"
                              : "var(--ec-status-neutral-bg)",
                          color:
                            view === v
                              ? "#fff"
                              : "var(--ec-status-neutral-text)",
                          border:
                            view === v
                              ? "1px solid transparent"
                              : "1px solid var(--ec-status-neutral-border)",
                          transition: "all 0.15s ease",
                          "& .MuiChip-label": { px: "10px" },
                        }}
                      />
                    ))}
                  </Stack>

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ height: 20, alignSelf: "center" }}
                  />

                  {/* QBO Connection toggle chip */}
                  <Chip
                    label="QBO Connection"
                    size="small"
                    onClick={() =>
                      setActiveTab(
                        activeTab === "connection" ? "search" : "connection",
                      )
                    }
                    sx={{
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 26,
                      backgroundColor:
                        activeTab === "connection"
                          ? "var(--ec-status-info-bg)"
                          : "var(--ec-status-neutral-bg)",
                      color:
                        activeTab === "connection"
                          ? "var(--ec-status-info-text)"
                          : "var(--ec-status-neutral-text)",
                      border: `1px solid ${
                        activeTab === "connection"
                          ? "var(--ec-status-info-border)"
                          : "var(--ec-status-neutral-border)"
                      }`,
                      "& .MuiChip-label": { px: "10px" },
                    }}
                  />

                  {/* Last Exports toggle chip */}

                  {/* 
                  <Chip
  key={VIEW.LOGS}
  label="Last Exports"
  size="small"
  onClick={() => {
    setView(VIEW.LOGS);
    fetchAudit();
  }}
  sx={{
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.72rem",
    height: 26,
    backgroundColor:
      view === VIEW.LOGS
        ? "var(--ec-color-primary, #1976d2)"
        : "var(--ec-status-neutral-bg)",
    color:
      view === VIEW.LOGS
        ? "#fff"
        : "var(--ec-status-neutral-text)",
    border:
      view === VIEW.LOGS
        ? "1px solid transparent"
        : "1px solid var(--ec-status-neutral-border)",
    transition: "all 0.15s ease",
    "& .MuiChip-label": { px: "10px" },
  }}
/> */}
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  {/* Column chooser */}
                  <Tooltip title="Show / hide columns">
                    <IconButton
                      size="small"
                      onClick={(e) => setColMenuEl(e.currentTarget)}
                      sx={{ color: "text.secondary" }}
                    >
                      <ViewColumnIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={colMenuEl}
                    open={Boolean(colMenuEl)}
                    onClose={() => setColMenuEl(null)}
                    slotProps={{
                      paper: { sx: { minWidth: 210, borderRadius: 2 } },
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        px: 2,
                        pt: 1.25,
                        pb: 0.5,
                        display: "block",
                        color: "text.disabled",
                      }}
                    >
                      Columns
                    </Typography>
                    <Divider sx={{ mb: 0.5 }} />
                    {ALL_INVOICING_COLUMNS.filter((c) => !c.locked).map(
                      (col) => (
                        <MenuItem
                          key={col.id}
                          onClick={() => toggleCol(col.id)}
                          sx={{ py: 0.4 }}
                        >
                          <Checkbox
                            size="small"
                            checked={visibleCols.includes(col.id)}
                            sx={{
                              p: 0.5,
                              mr: 0.5,
                              color: "primary.main",
                              "&.Mui-checked": { color: "primary.main" },
                            }}
                          />
                          <ListItemText
                            primaryTypographyProps={{ fontSize: "0.875rem" }}
                          >
                            {col.label}
                          </ListItemText>
                        </MenuItem>
                      ),
                    )}
                    <Divider sx={{ mt: 0.5 }} />
                    <Box sx={{ display: "flex", gap: 1, px: 2, py: 1 }}>
                      <Button
                        size="small"
                        onClick={selectAllCols}
                        sx={{ textTransform: "none", fontSize: "0.75rem" }}
                      >
                        Select all
                      </Button>
                      <Button
                        size="small"
                        onClick={clearAllCols}
                        sx={{ textTransform: "none", fontSize: "0.75rem" }}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Menu>

                  {/* Density */}
                  <Tooltip title="Row density">
                    <IconButton
                      size="small"
                      onClick={(e) => setDensityMenuEl(e.currentTarget)}
                      sx={{ color: "text.secondary" }}
                    >
                      <SettingsIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={densityMenuEl}
                    open={Boolean(densityMenuEl)}
                    onClose={() => setDensityMenuEl(null)}
                    slotProps={{
                      paper: { sx: { minWidth: 180, borderRadius: 2 } },
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        px: 2,
                        pt: 1.25,
                        pb: 0.5,
                        display: "block",
                        color: "text.disabled",
                      }}
                    >
                      Row density
                    </Typography>
                    {["compact", "comfortable", "spacious"].map((d) => (
                      <MenuItem
                        key={d}
                        onClick={() => {
                          setDensity(d);
                          setDensityMenuEl(null);
                        }}
                        sx={{ py: 0.75 }}
                      >
                        <Checkbox
                          size="small"
                          checked={density === d}
                          sx={{
                            p: 0.5,
                            mr: 0.5,
                            color: "primary.main",
                            "&.Mui-checked": { color: "primary.main" },
                          }}
                        />
                        <ListItemText
                          primaryTypographyProps={{
                            fontSize: "0.875rem",
                            textTransform: "capitalize",
                          }}
                        >
                          {d}
                        </ListItemText>
                      </MenuItem>
                    ))}
                  </Menu>

                  {/* Refresh */}
                  <Tooltip title="Refresh">
                    <IconButton
                      size="small"
                      onClick={fetchReport}
                      disabled={loading}
                      sx={{ color: "text.secondary" }}
                    >
                      <RefreshIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: 0.5, height: 24, alignSelf: "center" }}
                  />

                  {/* Get Report CTA */}
                  <Button
                    variant="contained"
                    size="small"
                    onClick={fetchReport}
                    disabled={loading}
                    startIcon={
                      loading ? (
                        <CircularProgress size={14} sx={{ color: "#fff" }} />
                      ) : (
                        <SearchIcon sx={{ fontSize: 15 }} />
                      )
                    }
                    sx={{
                      fontWeight: 700,
                      textTransform: "none",
                      height: 32,
                      px: 2,
                      borderRadius: "8px",
                      boxShadow: "none",
                      fontSize: "0.8rem",
                    }}
                  >
                    Get Report
                  </Button>
                </Stack>
              </Stack>

              {/* divider between title row and filter row */}
              <Divider sx={{ mt: 1.75, mb: 0 }} />

              {/* Filters */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 1.5 }}
              >
                <TextField
                  size="small"
                  type="date"
                  label="From Date"
                  InputLabelProps={{ shrink: true }}
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  sx={{
                    // width: 160,
                    flexGrow: 1,
                    "& .MuiInputBase-root": { height: 32, fontSize: "0.82rem" },
                  }}
                />

                {[VIEW.INVOICES, VIEW.DEPOSITS].includes(view) && (
                  <FormControl size="small" sx={{ minWidth: 150, flexGrow: 1 }}>
                    <InputLabel id="invoice-status-filter-label">Status</InputLabel>
                    <Select
                      labelId="invoice-status-filter-label"
                      value={statusFilter}
                      label="Status"
                      onChange={(event) => {
                        setStatusFilter(event.target.value);
                        setPage(0);
                        setSelected([]);
                      }}
                      sx={{ height: 32, fontSize: "0.82rem" }}
                    >
                      <MenuItem value={FINANCE_INVOICE_STATUS.ALL}>All statuses</MenuItem>
                      <MenuItem value={FINANCE_INVOICE_STATUS.OPEN}>Open</MenuItem>
                      <MenuItem value={FINANCE_INVOICE_STATUS.FAILED}>Failed</MenuItem>
                      <MenuItem value={FINANCE_INVOICE_STATUS.COMPLETED}>Completed</MenuItem>
                    </Select>
                  </FormControl>
                )}
                <TextField
                  size="small"
                  type="date"
                  label="To Date"
                  InputLabelProps={{ shrink: true }}
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  sx={{
                    // width: 160,
                    flexGrow: 1,
                    "& .MuiInputBase-root": { height: 32, fontSize: "0.82rem" },
                  }}
                />

                {(dateFrom || dateTo) && (
                  <Tooltip title="Clear date filters">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDateFrom("");
                        setDateTo("");
                      }}
                      sx={{ color: "text.secondary" }}
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ height: 24, alignSelf: "center" }}
                />

                {/* Export Selected Invoices view only */}
                {view === VIEW.INVOICES && (
                  <>
                    <Tooltip title="QBO Export">
                      <span><Button size="small" variant="contained" disabled={!qboExportEnabled || !qbo.connected || selected.length === 0} onClick={exportSelected} startIcon={<img src={QB_ICON} alt="" style={{ width: 13, height: 13 }} />} sx={{ fontWeight: 700, textTransform: "none", height: 30, px: 1.5, fontSize: "0.75rem", borderRadius: "8px", boxShadow: "none" }}>QBO Export ({selected.length})</Button></span>
                    </Tooltip>
                    <Tooltip title="Sync payment status for exported QBO invoices.">
                      <span><Button size="small" variant="outlined" disabled={!qboExportEnabled || !qbo.connected || qboPaymentBatchSyncing} onClick={syncAllQboInvoicePayments} startIcon={qboPaymentBatchSyncing ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />} sx={{ fontWeight: 700, textTransform: "none", height: 30, px: 1.5, fontSize: "0.75rem", borderRadius: "8px", boxShadow: "none" }}>Sync All QBO Invoice Payments</Button></span>
                    </Tooltip>
                    <Tooltip title="Generates a file and marks invoices as Exported.">
                      <span><Button size="small" variant="outlined" disabled={selected.length === 0} onClick={(e) => setFileExportMenuEl(e.currentTarget)}>File Export</Button></span>
                    </Tooltip>
                    <Menu anchorEl={fileExportMenuEl} open={Boolean(fileExportMenuEl)} onClose={() => setFileExportMenuEl(null)}>
                      <MenuItem onClick={() => { setFileExportMenuEl(null); runInvoiceFileExport("csv"); }}>Export Selected CSV</MenuItem>
                      <MenuItem onClick={() => { setFileExportMenuEl(null); runInvoiceFileExport("xlsx"); }}>Export Selected XLSX</MenuItem>
                    </Menu>
                    <Tooltip title="Downloads the current table only. Does not change export status.">
                      <Button size="small" variant="outlined" onClick={(e) => setReportMenuEl(e.currentTarget)}>Download Report</Button>
                    </Tooltip>
                    <Menu anchorEl={reportMenuEl} open={Boolean(reportMenuEl)} onClose={() => setReportMenuEl(null)}>
                      <MenuItem onClick={() => { setReportMenuEl(null); runInvoiceReportDownload("csv"); }}>Download Visible CSV</MenuItem>
                      <MenuItem onClick={() => { setReportMenuEl(null); runInvoiceReportDownload("xlsx"); }}>Download Visible XLSX</MenuItem>
                    </Menu>
                  </>
                )}

                {view === VIEW.INVOICES && (
                  <Tooltip title="Print all currently filtered invoices across table pages, without changing their status. Use Save as PDF in the browser print dialog.">
                    <span><Button size="small" variant="outlined" disabled={loading || !sortedRows.length} startIcon={<PrintIcon fontSize="small" />} onClick={() => printInvoiceRegister(sortedRows)}>Print List / PDF</Button></span>
                  </Tooltip>
                )}

                {view === VIEW.DEPOSITS && (
                  <>
                    <Tooltip title="Download every currently filtered deposit, across all table pages. Does not change invoice or QBO export status.">
                      <span><Button size="small" variant="outlined" disabled={loading || !sortedRows.length} startIcon={<FileDownloadIcon fontSize="small" />} onClick={() => downloadDepositRegisterCsv(sortedRows)}>Export Deposit List (CSV)</Button></span>
                    </Tooltip>
                    <Tooltip title="Print the filtered deposit register. Use your browser's Save as PDF option for a PDF report.">
                      <span><Button size="small" variant="outlined" disabled={loading || !sortedRows.length} startIcon={<PrintIcon fontSize="small" />} onClick={() => printDepositRegister(sortedRows)}>Print List / PDF</Button></span>
                    </Tooltip>
                  </>
                )}

                {/* Download CSV Exported view only */}
                {view === VIEW.EXPORTED && (
                  <>
                  <Tooltip title="Download Report">
                  <Button size="small" variant="outlined" onClick={async () => {
                      const accessToken = localStorage.getItem("accessToken");
                      const params = new URLSearchParams({ instanceUniqueId });
                      if (dateFrom) params.append("dateFrom", dateFrom);
                      if (dateTo) params.append("dateTo", dateTo);
                      try {
                        const res = await fetch(
                          `${SERVER}/invoices/exported/csv?${params.toString()}`,
                          { headers: { accessToken } },
                        );
                        if (!res.ok)
                          return alert(`Failed: ${await res.text()}`);
                        const blob = await res.blob();
                        const a = document.createElement("a");
                        a.href = URL.createObjectURL(blob);
                        a.download = `exported-invoices-${new Date()
                          .toISOString()
                          .slice(0, 10)}.csv`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                      } catch (e) {
                        alert("Export failed.");
                      }
                    }} startIcon={<FileDownloadIcon sx={{ fontSize: 15 }} />} sx={{ fontWeight: 600, textTransform: "none", height: 30, px: 1.5, fontSize: "0.75rem", borderRadius: "8px" }}>
                    Download CSV
                  </Button>
                  </Tooltip>
                  <Button size="small" variant="outlined" onClick={() => runInvoiceReportDownload("xlsx")}>Download XLSX</Button>
                  <Tooltip title="Returns invoices to the export queue. Does not undo external accounting entries.">
                    <span><Button size="small" variant="outlined" disabled={!isAdministrator || selected.length === 0} onClick={bulkUnexportSelected}>Bulk Unexport Selected</Button></span>
                  </Tooltip>
                  </>
                )}

                {/* Manage Customer Link */}
                <Tooltip
                  title={
                    selected.length !== 1
                      ? "Select exactly one invoice"
                      : "Manage Customer Link"
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={openLinkForSelected}
                      disabled={selected.length !== 1}
                      sx={{ color: "text.secondary" }}
                    >
                      <LinkIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>

                {/* Clear Selection */}
                {selected.length > 0 && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setSelected([])}
                    sx={{
                      fontWeight: 600,
                      textTransform: "none",
                      height: 30,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Clear ({selected.length})
                  </Button>
                )}

                <Box sx={{ ml: "auto" }}>
                  <Typography
                    variant="caption"
                    sx={{ color: C.textMuted, fontWeight: 500 }}
                  >
                    ${visibleSummary.total.toFixed(2)} · {visibleSummary.count} rows
                  </Typography>
                </Box>
              </Stack>

              {/* QBO Connection panel */}
              {activeTab === "connection" && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 2,
                    borderRadius: "8px",
                    bgcolor: qbo.connected ? "#e8f5e9" : "#fff8e1",
                    border: "1px solid",
                    borderColor: qbo.connected ? "#c8e6c9" : "#ffe0b2",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <img
                      src={QB_ICON}
                      alt="QuickBooks"
                      style={{ width: 28, height: 28, borderRadius: 6 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        QuickBooks Online
                      </Typography>
                      <Typography variant="body2" sx={{ color: C.textSec }}>
                        {qbo.checking
                          ? "Checking connection…"
                          : qbo.connected
                          ? `Connected${
                              qbo.realmId ? ` (Realm ${qbo.realmId})` : ""
                            }${
                              qbo.env
                                ? ` · ${String(qbo.env).toUpperCase()} mode`
                                : ""
                            }`
                          : `Not connected${
                              qbo.env
                                ? ` · active mode: ${String(
                                    qbo.env,
                                  ).toUpperCase()}`
                                : ""
                            }`}
                      </Typography>
                      {!qboExportEnabled && (
                        <Typography
                          variant="caption"
                          sx={{ color: "#b26a00", display: "block", mt: 0.5 }}
                        >
                          QBO exports are disabled in Global Settings →
                          Integrations.
                        </Typography>
                      )}
                      {qbo.modeMismatch && qbo.suggestedEnv && (
                        <Typography
                          variant="caption"
                          sx={{ color: "#b26a00", display: "block", mt: 0.5 }}
                        >
                          Mode mismatch — available:{" "}
                          {String(qbo.suggestedEnv).toUpperCase()}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={fetchQboStatus}
                      disabled={qbo.checking}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        height: 32,
                      }}
                    >
                      Refresh
                    </Button>
                    {qbo.modeMismatch && qbo.suggestedEnv && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => switchQboMode(qbo.suggestedEnv)}
                        disabled={qbo.checking}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          height: 32,
                        }}
                      >
                        Use {String(qbo.suggestedEnv).toUpperCase()} mode
                      </Button>
                    )}
                    {!qbo.connected ? (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleQboConnect}
                        startIcon={
                          <img
                            src={QB_ICON}
                            alt=""
                            style={{ width: 14, height: 14 }}
                          />
                        }
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          height: 32,
                          boxShadow: "none",
                        }}
                      >
                        Connect to QuickBooks
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        disabled
                        startIcon={
                          <img
                            src={QB_ICON}
                            alt=""
                            style={{ width: 14, height: 14 }}
                          />
                        }
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          height: 32,
                          backgroundColor: "#2ca01c",
                          boxShadow: "none",
                        }}
                      >
                        Connected
                      </Button>
                    )}
                  </Stack>
                </Box>
              )}
            </UITableToolbar>

            <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
              {view === VIEW.LOGS ? (
                // <TableContainer sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
                <TableContainer
                  sx={{
                    flex: 1,
                    overflow: "auto",
                    minHeight: 0,
                    //  borderRadius: 0,
                    borderRadius: "0 !important",
                    "&::-webkit-scrollbar": { width: 6, height: 6 },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "var(--ec-color-border-strong)",
                      borderRadius: 99,
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  {/* <Table stickyHeader size="small" sx={{ minWidth: 800 }}> */}
                  <Table
                    stickyHeader
                    size="small"
                    sx={{ minWidth: 800, width: "100%" }}
                  >
                    <TableHead>
                      <TableRow>
                        {[
                          "When",
                          "Invoice ID",
                          "Status",
                          "Code",
                          "Message",
                        ].map((h) => (
                          <TableCell
                            key={h}
                            sx={{
                              backgroundColor: C.headerBg,
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: "0.78rem",
                              py: 1.25,
                              px: 1.5,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {auditLoading ? (
                        [...Array(8)].map((_, i) => (
                          <TableRow key={i}>
                            {[...Array(5)].map((_, j) => (
                              <TableCell key={j}>
                                <Skeleton variant="text" width="80%" />
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : auditRows.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            align="center"
                            sx={{ py: 10, borderBottom: "none" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 64,
                                  height: 64,
                                  borderRadius: "50%",
                                  backgroundColor:
                                    "var(--ec-color-surface-muted)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <FolderOffIcon
                                  sx={{
                                    fontSize: 32,
                                    color: "var(--ec-color-text-muted)",
                                  }}
                                />
                              </Box>
                              <Typography
                                variant="h6"
                                fontWeight={600}
                                color="text.secondary"
                              >
                                No export logs found
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        auditRows.map((r) => (
                          <TableRow key={r.id} hover>
                            <TableCell
                              sx={{
                                py: densitySettings[density].py,
                                px: 1.5,
                                fontSize: "0.82rem",
                                borderBottom: `1px solid ${C.border}`,
                              }}
                            >
                              {new Date(r.startedAt).toLocaleString()}
                            </TableCell>
                            <TableCell
                              sx={{
                                py: densitySettings[density].py,
                                px: 1.5,
                                fontSize: "0.82rem",
                                fontFamily: "monospace",
                                borderBottom: `1px solid ${C.border}`,
                              }}
                            >
                              {r.entityId}
                            </TableCell>
                            <TableCell
                              sx={{
                                py: densitySettings[density].py,
                                px: 1.5,
                                borderBottom: `1px solid ${C.border}`,
                              }}
                            >
                              <UIStatusChip
                                label={r.status}
                                tone={
                                  r.status === "success" ? "success" : "error"
                                }
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                py: densitySettings[density].py,
                                px: 1.5,
                                fontSize: "0.82rem",
                                fontFamily: "monospace",
                                borderBottom: `1px solid ${C.border}`,
                              }}
                            >
                              {r.errorCode || "—"}
                            </TableCell>
                            <TableCell
                              sx={{
                                py: densitySettings[density].py,
                                px: 1.5,
                                fontSize: "0.82rem",
                                maxWidth: 400,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                borderBottom: `1px solid ${C.border}`,
                              }}
                            >
                              {r.errorMessage || "—"}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                // {/* <TableContainer sx={{ height: "100%" }}> */}
                <TableContainer
                  sx={{
                    flex: 1,
                    overflow: "auto",
                    minHeight: 0,
                    //  borderRadius: 0,
                    borderRadius: "0 !important",
                    "&::-webkit-scrollbar": { width: 6, height: 6 },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "var(--ec-color-border-strong)",
                      borderRadius: 99,
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  {/* <Table stickyHeader size="small"> */}
                  <Table
                    stickyHeader
                    size="small"
                    sx={{ minWidth: 800, width: "100%" }}
                  >
                    <TableHead>
                      <TableRow>
                        {/* Checkbox column */}
                        {view === VIEW.INVOICES && (
                          <TableCell
                            padding="checkbox"
                            sx={{ backgroundColor: C.headerBg, width: 48 }}
                          >
                            <Checkbox
                              size="small"
                              checked={
                                pagedRows.length > 0 &&
                                pagedRows.every((r) => selected.includes(r.id))
                              }
                              indeterminate={
                                pagedRows.some((r) =>
                                  selected.includes(r.id),
                                ) &&
                                !pagedRows.every((r) => selected.includes(r.id))
                              }
                              onChange={(e) => {
                                if (e.target.checked)
                                  setSelected((prev) => [
                                    ...new Set([
                                      ...prev,
                                      ...pagedRows.map((r) => r.id),
                                    ]),
                                  ]);
                                else
                                  setSelected((prev) =>
                                    prev.filter(
                                      (id) =>
                                        !pagedRows.find((r) => r.id === id),
                                    ),
                                  );
                              }}
                              sx={{
                                color: "rgba(255,255,255,0.6)",
                                "&.Mui-checked": { color: "#fff" },
                                "&.MuiCheckbox-indeterminate": {
                                  color: "#fff",
                                },
                              }}
                            />
                          </TableCell>
                        )}
                        {ALL_INVOICING_COLUMNS.filter((c) =>
                          visibleCols.includes(c.id),
                        ).map((col) => (
                          <TableCell
                            key={col.id}
                            onClick={
                              col.sortable
                                ? () => handleSort(col.id)
                                : undefined
                            }
                            sx={{
                              backgroundColor: C.headerBg,
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: "0.78rem",
                              whiteSpace: "nowrap",
                              minWidth: col.minWidth,
                              cursor: col.sortable ? "pointer" : "default",
                              userSelect: "none",
                              py: 1.25,
                              px: 1.5,
                              "&:hover": col.sortable
                                ? { backgroundColor: C.headerBgHover }
                                : {},
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              {col.label}
                              {col.sortable &&
                                (sortBy === col.id ? (
                                  sortDir === "asc" ? (
                                    <ArrowUpwardIcon
                                      sx={{ fontSize: 13, opacity: 0.9 }}
                                    />
                                  ) : (
                                    <ArrowDownwardIcon
                                      sx={{ fontSize: 13, opacity: 0.9 }}
                                    />
                                  )
                                ) : (
                                  <UnfoldMoreIcon
                                    sx={{ fontSize: 13, opacity: 0.35 }}
                                  />
                                ))}
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        [...Array(8)].map((_, i) => (
                          <TableRow key={i}>
                            {view === VIEW.INVOICES && (
                              <TableCell padding="checkbox">
                                <Skeleton
                                  variant="rectangular"
                                  width={18}
                                  height={18}
                                />
                              </TableCell>
                            )}
                            {ALL_INVOICING_COLUMNS.filter((c) =>
                              visibleCols.includes(c.id),
                            ).map((col) => (
                              <TableCell key={col.id}>
                                <Skeleton variant="text" width="80%" />
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : pagedRows.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={
                              visibleCols.length +
                              (view === VIEW.INVOICES ? 1 : 0)
                            }
                            align="center"
                            sx={{ py: 10, borderBottom: "none" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 64,
                                  height: 64,
                                  borderRadius: "50%",
                                  backgroundColor:
                                    "var(--ec-color-surface-muted)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <FolderOffIcon
                                  sx={{
                                    fontSize: 32,
                                    color: "var(--ec-color-text-muted)",
                                  }}
                                />
                              </Box>
                              <Box sx={{ textAlign: "center" }}>
                                <Typography
                                  variant="h6"
                                  fontWeight={600}
                                  color="text.secondary"
                                  gutterBottom
                                >
                                  {view === VIEW.DEPOSITS
                                    ? "No deposits found"
                                    : "No invoices found"}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.disabled"
                                  sx={{ maxWidth: 340 }}
                                >
                                  {dateFrom || dateTo || statusFilter !== FINANCE_INVOICE_STATUS.ALL
                                    ? `No ${view === VIEW.DEPOSITS ? "deposits" : "invoices"} match your current filters.`
                                    : `No data yet. Click Get Report to load ${view === VIEW.DEPOSITS ? "deposits" : "invoices"}.`}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={1.5} mt={0.5}>
                                {(dateFrom || dateTo) && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={
                                      <ClearIcon sx={{ fontSize: 14 }} />
                                    }
                                    onClick={() => {
                                      setDateFrom("");
                                      setDateTo("");
                                    }}
                                    sx={{
                                      textTransform: "none",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Clear Filters
                                  </Button>
                                )}
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={
                                    <SearchIcon sx={{ fontSize: 14 }} />
                                  }
                                  onClick={fetchReport}
                                  sx={{
                                    textTransform: "none",
                                    fontWeight: 700,
                                  }}
                                >
                                  Get Report
                                </Button>
                              </Stack>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        pagedRows.map((row) => (
                          <TableRow
                            key={row.id}
                            hover
                            selected={selected.includes(row.id)}
                            sx={{
                              "&.Mui-selected": {
                                backgroundColor: "action.selected",
                              },
                              "&.Mui-selected:hover": {
                                backgroundColor: "action.selected",
                              },
                            }}
                          >
                            {view === VIEW.INVOICES && (
                              <TableCell padding="checkbox">
                                <Checkbox
                                  size="small"
                                  checked={selected.includes(row.id)}
                                  onChange={(e) =>
                                    setSelected((prev) =>
                                      e.target.checked
                                        ? [...prev, row.id]
                                        : prev.filter((id) => id !== row.id),
                                    )
                                  }
                                />
                              </TableCell>
                            )}
                            {ALL_INVOICING_COLUMNS.filter((c) =>
                              visibleCols.includes(c.id),
                            ).map((col) => (
                              <TableCell
                                key={col.id}
                                sx={{
                                  py: densitySettings[density].py,
                                  px: 1.5,
                                  fontSize: densitySettings[density].fontSize,
                                  borderBottom: `1px solid ${C.border}`,
                                }}
                              >
                                {renderCell(col.id, row)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            <Box sx={{ flexShrink: 0, borderTop: `1px solid ${C.border}` }}>
              <TablePagination
                component="div"
                count={sortedRows.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  fontSize: "0.8rem",
                  "& .MuiTablePagination-toolbar": { minHeight: 44 },
                }}
              />
            </Box>
          </UITableShell>
        </Box>
      </div>

      <QboCustomerLinkDialog
        open={qboLinkOpen}
        onClose={() => setQboLinkOpen(false)}
        instanceUniqueId={instanceUniqueId}
        customerId={qboLinkCtx.customerId}
        customerName={qboLinkCtx.customerName}
        invoiceId={qboLinkCtx.invoiceId}
        onLinked={async () => {
          if (qboLinkCtx.invoiceId) {
            setRows((prev) =>
              prev.map((r) =>
                String(r.id) === String(qboLinkCtx.invoiceId)
                  ? {
                      ...r,
                      _qboLinkStatus: "linked",
                      qboSyncError: /CUSTOMER\s*NOT\s*LINKED|CUSTOMER_UNMAPPED/i.test(
                        String(r.qboSyncError || ""),
                      )
                        ? null
                        : r.qboSyncError,
                      qboExportStatus:
                        String(r.qboExportStatus || "").toLowerCase() ===
                        "failed"
                          ? "pending"
                          : r.qboExportStatus,
                    }
                  : r,
              ),
            );
          }
          await fetchReport();
        }}
      />
    </div>
  );
}
